import React, { useEffect, useRef } from 'react';

/**
 * Canvas animé — réseau logistique Europe ↔ Afrique
 * Reproduit fidèlement la maquette auth.js initCanvas()
 */
export default function AuthCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    const ctx = cv.getContext('2d');
    let W, H;
    let pulseT = 0;
    let dashOffset = 0;
    let mouseX = -999, mouseY = -999;
    let rafId;

    const CITIES = [
      { rx: .10, ry: .72, r: 6, col: '#F0C97A', pulse: true,  label: 'Paris' },
      { rx: .18, ry: .55, r: 5, col: '#F0C97A', pulse: false, label: 'Lyon' },
      { rx: .24, ry: .78, r: 5, col: '#F0C97A', pulse: false, label: 'Marseille' },
      { rx: .08, ry: .42, r: 4, col: '#F0C97A', pulse: false, label: 'Bruxelles' },
      { rx: .73, ry: .24, r: 6, col: '#7ecfc2', pulse: true,  label: 'Dakar' },
      { rx: .82, ry: .40, r: 5, col: '#7ecfc2', pulse: false, label: 'Abidjan' },
      { rx: .64, ry: .46, r: 5, col: '#7ecfc2', pulse: false, label: 'Bamako' },
      { rx: .92, ry: .30, r: 4, col: '#7ecfc2', pulse: false, label: 'Lagos' },
      { rx: .55, ry: .60, r: 4, col: '#7ecfc2', pulse: false, label: 'Conakry' },
    ];

    const ROUTES = [
      { f: 0, t: 4, col: 'rgba(126,207,194,.28)', w: 1.8 },
      { f: 1, t: 5, col: 'rgba(240,201,122,.22)', w: 1.5 },
      { f: 2, t: 6, col: 'rgba(126,207,194,.2)',  w: 1.5 },
      { f: 3, t: 4, col: 'rgba(240,201,122,.18)', w: 1.2 },
      { f: 0, t: 7, col: 'rgba(126,207,194,.16)', w: 1.2 },
      { f: 1, t: 8, col: 'rgba(240,201,122,.15)', w: 1.0 },
    ];

    const PACKETS = ROUTES.map((r, i) => ({
      route: r,
      t: (i * .17) % 1,
      speed: .0014 + i * .0003,
      col: i % 2 === 0 ? '#F0C97A' : '#7ecfc2',
      size: i % 3 === 0 ? 9 : 7,
    }));

    const parent = cv.parentElement;
    const handleMouseMove = (e) => {
      const rect = cv.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    const handleMouseLeave = () => { mouseX = -999; mouseY = -999; };
    parent.addEventListener('mousemove', handleMouseMove);
    parent.addEventListener('mouseleave', handleMouseLeave);

    const resize = () => {
      W = cv.width = cv.offsetWidth;
      H = cv.height = cv.offsetHeight;
    };
    const getPos = (c) => ({ x: c.rx * W, y: c.ry * H });
    const bezierPoint = (a, b, t) => {
      const cpx = (a.x + b.x) / 2;
      const cpy = Math.min(a.y, b.y) - Math.abs(b.x - a.x) * 0.42;
      const mt = 1 - t;
      return {
        x: mt * mt * a.x + 2 * mt * t * cpx + t * t * b.x,
        y: mt * mt * a.y + 2 * mt * t * cpy + t * t * b.y,
      };
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, W, H);
      pulseT += 0.022;
      dashOffset -= 0.6;

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,.022)';
      ctx.lineWidth = 1;
      const gsx = Math.round(W / 20), gsy = Math.round(H / 12);
      for (let x = 0; x <= W; x += gsx) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y <= H; y += gsy) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

      // Routes
      ROUTES.forEach(route => {
        const f = getPos(CITIES[route.f]);
        const t = getPos(CITIES[route.t]);
        const cpx = (f.x + t.x) / 2;
        const cpy = Math.min(f.y, t.y) - Math.abs(t.x - f.x) * 0.42;
        const mx = (f.x + t.x) / 2;
        const my = (f.y + t.y) / 2;
        const dist = Math.sqrt((mouseX - mx) ** 2 + (mouseY - my) ** 2);
        if (dist < 120) {
          ctx.beginPath();
          ctx.moveTo(f.x, f.y);
          ctx.quadraticCurveTo(cpx, cpy, t.x, t.y);
          ctx.strokeStyle = route.col.replace(/[\d.]+\)$/, '0.55)');
          ctx.lineWidth = route.w + 2;
          ctx.setLineDash([]);
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.moveTo(f.x, f.y);
        ctx.quadraticCurveTo(cpx, cpy, t.x, t.y);
        ctx.strokeStyle = route.col;
        ctx.lineWidth = route.w;
        ctx.setLineDash([8, 5]);
        ctx.lineDashOffset = dashOffset;
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // Cities
      CITIES.forEach(city => {
        const p = getPos(city);
        const dist = Math.sqrt((mouseX - p.x) ** 2 + (mouseY - p.y) ** 2);
        const near = dist < 70;
        if (city.pulse || near) {
          const scale = city.pulse
            ? 1 + .45 * Math.abs(Math.sin(pulseT))
            : 1 + .6 * (1 - Math.min(dist / 70, 1));
          ctx.beginPath();
          ctx.arc(p.x, p.y, city.r * 4 * scale, 0, Math.PI * 2);
          ctx.fillStyle = city.col + '14';
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, city.r * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = city.col + '30';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, near ? city.r * 1.4 : city.r, 0, Math.PI * 2);
        ctx.fillStyle = city.col;
        ctx.fill();
        if (near) {
          ctx.font = '600 11px system-ui';
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'center';
          ctx.fillText(city.label, p.x, p.y - city.r * 2.5);
        }
      });

      // Packets
      PACKETS.forEach(pk => {
        pk.t = (pk.t + pk.speed) % 1;
        const f = getPos(CITIES[pk.route.f]);
        const t = getPos(CITIES[pk.route.t]);
        const pos = bezierPoint(f, t, pk.t);
        ctx.save();
        ctx.shadowColor = pk.col;
        ctx.shadowBlur = 6;
        const s = pk.size;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(pos.x - s / 2, pos.y - s / 3, s, s * .7, 2);
        } else {
          ctx.rect(pos.x - s / 2, pos.y - s / 3, s, s * .7);
        }
        ctx.fillStyle = pk.col;
        ctx.globalAlpha = .88;
        ctx.fill();
        ctx.globalAlpha = .35;
        ctx.strokeStyle = 'rgba(15,23,42,.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(pos.x - s / 2, pos.y);
        ctx.lineTo(pos.x + s / 2, pos.y);
        ctx.moveTo(pos.x, pos.y - s / 3);
        ctx.lineTo(pos.x, pos.y + s / 3);
        ctx.stroke();
        ctx.restore();
      });

      // Particles
      ctx.save();
      const now = Date.now() * .001;
      for (let i = 0; i < 18; i++) {
        const px = ((Math.sin(now * .3 + i * 2.1) * .5 + .5)) * W;
        const py = ((Math.cos(now * .2 + i * 1.7) * .5 + .5)) * H;
        const a = .08 + .06 * Math.sin(now + i);
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = i % 2 === 0 ? `rgba(240,201,122,${a})` : `rgba(126,207,194,${a})`;
        ctx.fill();
      }
      ctx.restore();

      rafId = requestAnimationFrame(drawFrame);
    };

    const resizeObs = new ResizeObserver(() => resize());
    resizeObs.observe(parent);
    resize();
    drawFrame();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObs.disconnect();
      parent.removeEventListener('mousemove', handleMouseMove);
      parent.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="bg-canvas" />;
}
